import React, { useState, useEffect} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Plus, Edit, Trash2, Star } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import DeleteModal from "../../components/admin/DeleteModal";
import { useNavigate } from "react-router-dom";
import SearchBar from "../ui/search-bar";
import { fetchTestimonialsDashboard, removeTestimonial } from "@/hooks/useTestimonials";

const TestimonialManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    testimonial: null,
  });
  const [testimonialList, setTestimonialList] = useState([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  const getTestimonials = async () => {
    try {
      const res = await fetchTestimonialsDashboard();
      setTestimonialList(res.testimonial || res);
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.message || "Failed to load testimonials.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    getTestimonials();
  }, []);

  const filteredTestimonials = testimonialList.filter(
    (t) =>
      t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.projectType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (id) => {
    try {
      // console.log(id)
      await removeTestimonial(id)
      setDeleteModal({ isOpen: false, testimonial: null });
      toast({
        title: "Testimonial Deleted!",
        description: `${deleteModal.testimonial.name}'s testimonial has been removed successfully.`,
      });
      await getTestimonials()
    } catch (error) {
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to delete testimonial. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">Testimonials Management</h2>
          <p className="text-muted-foreground">
            Manage your client testimonials
          </p>
        </div>
        <Button
          className="bg-purple-600 hover:bg-purple-700"
          onClick={() => navigate("/admin/testimonials/add")}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Testimonial
        </Button>
      </div>

      {/* Search */}
      <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

      {/* Testimonials Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {filteredTestimonials.map((t, index) => (
            <motion.div
              key={t._id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ delay: index * 0.1, duration: 0.3 }}
              whileHover={{ y: -5 }}
            >
              <Card className="hover:shadow-lg transition-all duration-300">
                <div className="aspect-square overflow-hidden rounded-t-lg bg-muted flex items-center justify-center">
                  <img
                    src={t.images?.url}
                    alt={t.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-4 space-y-3">
                  <div>
                    <h3 className="font-semibold text-lg">{t.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t.position} @ {t.company}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t.country}
                    </p>
                  </div>

                  <p className="text-sm line-clamp-3">{t.message}</p>

                  {/* Rating */}
                  <div className="flex gap-1">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star
                        key={i}
                        className="w-4 h-4 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>

                  {/* Tags */}
                  <div className="flex justify-between items-center">
                    <Badge variant="outline">{t.country} Client</Badge>
                    <Badge variant="secondary">{t.projectTitle}</Badge>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="hover:bg-blue-50 dark:hover:bg-blue-950/20"
                      onClick={() =>
                        navigate(`/admin/testimonials/edit/${t?._id}`)
                      }
                    >
                      <Edit className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="hover:bg-red-50 dark:hover:bg-red-950/20"
                      onClick={() =>
                        setDeleteModal({ isOpen: true, testimonial: t })
                      }
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, t: null })}
        onConfirm={() => handleDelete(deleteModal.testimonial?._id)}
        title="Delete Testimonial"
        description="This will permanently remove the testimonial."
        itemName={deleteModal.t?.name}
      />
    </motion.div>
  );
};

export default TestimonialManagement;
